import * as z from 'zod/v4';

import { drawStateClientEnum } from '../draw/entry.js';
import { apiErrorSchema } from '../error/entry.js';
import { tipsinfoEnum } from '../tipsinfo/entry.js';
import { formData, repeatableOfType } from '../utils/entry.js';
import { sessionStoreBaseSchema, sessionStoreDataBaseSchema } from './sessionStore.js';

const outcomeLabelEnum = z.enum(['Outcome', 'LocalDistribution']);
const analysisEnum = z.enum(['Tip', 'Analysis']);

export type Analysis = z.infer<typeof analysisEnum>;

const safeJSONParse = (data: string, defaultReturn = {}): typeof defaultReturn => {
  try {
    return JSON.parse(data) as typeof defaultReturn;
  } catch {
    return defaultReturn;
  }
};

/**
 * JSON schema that represents coupon settings.
 * Handles fallback values if data is malicious or undefined
 */
const couponSettingsJsonSchema = z.object({
  outcomeLabel: outcomeLabelEnum.catch('Outcome'),
  gameAnalysis: z.array(analysisEnum).catch([]),
  tipsinfo: z.array(tipsinfoEnum).catch([]),
  correction: z.array(drawStateClientEnum).catch([]),
});

export type CouponSettings = z.infer<typeof couponSettingsJsonSchema>;

export type SettingsKeys = 'PLAY' | 'RESULT' | 'MY_BETS';

/**
 * JSON schema to parse data from session storage.
 */
export const sessionStoreCouponSettingsSchema = z.object({
  ...sessionStoreBaseSchema.shape,
  data: z.string().transform((value) => {
    const parsed = safeJSONParse(value);
    const base = sessionStoreDataBaseSchema.parse(parsed);
    const settings = couponSettingsJsonSchema.parse(parsed);

    return {
      ...base,
      ...settings,
    };
  }),
});

export const couponSettingsResponse = {
  parse: (data: unknown) => z.union([sessionStoreCouponSettingsSchema, apiErrorSchema]).parse(data),
} as const;

export const createCouponSettingsResponse = {
  // Any object is considered Ok since the payload isn't interesting
  parse: (data: unknown) => z.union([apiErrorSchema, z.object({})]).parse(data),
} as const;

const tipsinfoToggleSchema = z.literal('on');
const analysisOptionsEnum = z.enum(['Full', analysisEnum.enum.Tip]);

/**
 * Parses HTML FormData to the corresponding JSON schema
 */
export const couponSettingsFormDataSchema = formData({
  outcomeLabel: outcomeLabelEnum.catch('Outcome'),
  gameAnalysis: analysisOptionsEnum.optional().catch(undefined),
  tipsinfo: repeatableOfType(tipsinfoEnum).catch([]),
  tipsinfoFull: tipsinfoToggleSchema.optional().catch(undefined),
  correction: repeatableOfType(drawStateClientEnum).catch([]),
}).transform((data) => {
  let gameAnalysis: Analysis[] = [];

  if (data.gameAnalysis === 'Full') {
    gameAnalysis = analysisEnum.options;
  } else if (data.gameAnalysis === 'Tip') {
    gameAnalysis.push('Tip');
  }

  // For now all products will have the same default tipsinfo settings,
  // and the UI should use the definition fo which values that are supported by the current product
  const tipsinfo: z.infer<typeof tipsinfoEnum>[] = data.tipsinfo;

  return {
    outcomeLabel: data.outcomeLabel,
    gameAnalysis,
    tipsinfo,
    tipsinfoFull: data.tipsinfoFull,
    correction: data.correction,
  } satisfies CouponSettings & { tipsinfoFull?: z.infer<typeof tipsinfoToggleSchema> };
});

export const couponSettingsFormData = {
  parse: (data: unknown) => couponSettingsFormDataSchema.parse(data),
} as const;
